import { z } from 'zod';

export const ErrorCode = z.enum([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION',
  'QUOTA_EXCEEDED',
  'VERSION_CONFLICT',
  'RATE_LIMITED',
  'CONFLICT',
  'INTERNAL',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ApiErrorBody = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

const STATUS: Record<ErrorCode, number> = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION: 400,
  QUOTA_EXCEEDED: 413,
  VERSION_CONFLICT: 409,
  RATE_LIMITED: 429,
  CONFLICT: 409,
  INTERNAL: 500,
};

/** Throw anywhere in a route handler; the route wrapper turns it into `{ error: { code, message, details } }`. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: unknown;
  /** extra top-level fields merged into the body (e.g. VERSION_CONFLICT carries { version, doc }) */
  readonly extra: Record<string, unknown> | undefined;

  constructor(code: ErrorCode, message?: string, details?: unknown, extra?: Record<string, unknown>) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.status = STATUS[code];
    this.details = details;
    this.extra = extra;
  }

  toBody(): ApiErrorBody & Record<string, unknown> {
    const body: ApiErrorBody & Record<string, unknown> = {
      error: { code: this.code, message: this.message, ...(this.details !== undefined ? { details: this.details } : {}) },
    };
    if (this.extra) Object.assign(body, this.extra);
    return body;
  }

  toResponse(init?: ResponseInit): Response {
    return Response.json(this.toBody(), { ...init, status: this.status });
  }

  static isApiError(e: unknown): e is ApiError {
    return e instanceof ApiError || (typeof e === 'object' && e !== null && (e as { name?: string }).name === 'ApiError');
  }
}

export const unauthorized = (msg = 'Sign in required') => new ApiError('UNAUTHORIZED', msg);
export const forbidden = (msg = 'Not allowed') => new ApiError('FORBIDDEN', msg);
export const notFound = (what = 'Resource') => new ApiError('NOT_FOUND', `${what} not found`);
export const validation = (msg: string, details?: unknown) => new ApiError('VALIDATION', msg, details);
export const quotaExceeded = (msg: string, details?: unknown) => new ApiError('QUOTA_EXCEEDED', msg, details);
export const rateLimited = (msg = 'Too many requests') => new ApiError('RATE_LIMITED', msg);
